import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Calendar, MapPin, Quote } from "lucide-react";
import { Container, Icon, PageHero, Reveal, SectionHeading, ArrowFwd } from "../components/shared/Shared";
import { useLang } from "../context/LanguageContext";
import { MissionVision } from "./Home";
import { GroupSection, Testimonials } from "./HomeSections";

export const DirectorMessage = () => {
  const { t, c } = useLang();
  const d = c.DIRECTOR;
  return (
    <section className="relative overflow-hidden py-24" data-testid="director-message-section">
      <div className="absolute inset-0 hero-grid-dark opacity-40" />
      <div className="absolute -end-32 top-1/3 hidden h-[420px] w-[420px] rounded-full bg-brand-teal/10 blur-3xl lg:block" />
      <Container className="relative grid items-center gap-14 lg:grid-cols-12">
        <Reveal className="relative mx-auto w-full max-w-[420px] lg:col-span-5">
          <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-brand-teal/25 to-brand-orange/25 blur-2xl" />
          <div className="absolute -start-4 -top-4 h-24 w-24 rounded-2xl border-4 border-brand-orange/70" />
          <div className="absolute -bottom-4 -end-4 h-32 w-32 rounded-2xl bg-brand-teal/15" />
          <div className="relative overflow-hidden rounded-[1.75rem] shadow-2xl shadow-brand-navy/25 ring-1 ring-black/5">
            <img src={d.image} alt={d.name} className="aspect-[4/5] w-full object-cover object-top" data-testid="director-image" />
            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-brand-navy/90 via-brand-navy/40 to-transparent p-6 pt-16 text-white">
              <p className="font-heading text-xl font-bold">{d.name}</p>
              <p className="mt-1 text-sm text-slate-200">{d.title}</p>
            </div>
          </div>
        </Reveal>
        <div className="lg:col-span-7">
          <SectionHeading eyebrow={t.about.directorEyebrow} title={t.about.directorTitle} />
          <Reveal delay={100} className="relative mt-8 rounded-2xl border-s-4 border-brand-orange bg-slate-50 p-6 sm:p-8">
            <Quote className="absolute -top-4 end-6 h-9 w-9 text-brand-orange/40 rtl:-scale-x-100" />
            <p className="font-heading text-xl font-semibold leading-relaxed text-brand-navy sm:text-2xl">&ldquo;{d.quote}&rdquo;</p>
          </Reveal>
          <Reveal delay={160} className="mt-8 space-y-5 leading-relaxed text-slate-600">
            {d.message.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
            <div className="pt-2">
              <p className="text-slate-500">{d.signoff}</p>
              <p className="mt-2 font-heading text-2xl font-bold text-brand-navy">{d.name}</p>
              <p className="text-sm font-medium text-brand-teal">{d.title}</p>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
};

export const About = () => {
  const { t, c } = useLang();
  const a = t.about;
  return (
    <>
      <PageHero title={a.title} subtitle={a.subtitle} crumbs={[{ label: a.title }]} image={c.IMAGES.about} />
      <section className="py-24" data-testid="about-intro">
        <Container className="grid items-center gap-14 lg:grid-cols-2">
          <div>
            <SectionHeading eyebrow={a.who} title={c.COMPANY.name} description={a.intro} />
            <Reveal delay={100}>
              <p className="mt-5 leading-relaxed text-slate-600">{a.sister}</p>
              <ul className="mt-8 grid gap-3 sm:grid-cols-2">{a.points.map((p) => <li key={p} className="flex items-start gap-2 text-sm font-medium text-slate-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" />{p}</li>)}</ul>
              <Link to="/contact" className="btn-primary mt-9 bg-brand-teal text-white hover:bg-brand-teal-dark">{a.work} <ArrowFwd className="h-4 w-4" /></Link>
            </Reveal>
          </div>
          <Reveal className="relative">
            <div className="overflow-hidden rounded-3xl shadow-2xl shadow-brand-navy/10"><img src={c.IMAGES.team} alt="Jonaren team" className="aspect-[4/3] w-full object-cover" /></div>
            <div className="absolute -bottom-8 -start-4 rounded-2xl bg-brand-navy p-6 text-white shadow-xl sm:-start-8"><p className="font-heading text-4xl font-bold">2010</p><p className="text-sm text-slate-300">{a.heritage}</p></div>
          </Reveal>
        </Container>
      </section>
      <MissionVision />
      <DirectorMessage />
      <section className="py-24" data-testid="about-values">
        <Container>
          <SectionHeading eyebrow={a.whyEyebrow} title={a.whyTitle} align="center" />
          <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {c.WHY_US.map((w, i) => (
              <Reveal key={w.title} delay={i * 80} className="card-hover rounded-2xl border border-slate-200 bg-white p-7">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-orange/10 text-brand-orange"><Icon name={w.icon} className="h-6 w-6" /></span>
                <h3 className="mt-5 font-heading text-lg font-bold text-brand-navy">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">{w.description}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      <GroupSection />
      <Testimonials />
    </>
  );
};

export const Group = () => {
  const { t, c } = useLang();
  const g = t.group;
  return (
    <>
      <PageHero title={g.title} subtitle={c.GROUP.intro} crumbs={[{ label: g.title }]} image={c.IMAGES.heroAlt} />
      <section className="py-24">
        <Container>
          <SectionHeading eyebrow={g.companiesEyebrow} title={g.companiesTitle} description={g.companiesDesc} align="center" />
          <div className="mt-14 grid gap-8 lg:grid-cols-2">
            {c.GROUP.companies.map((co, i) => (
              <Reveal key={co.name} delay={i * 120} className="card-hover flex flex-col overflow-hidden rounded-3xl bg-white shadow-sm ring-1 ring-slate-100">
                <div className="relative h-56 overflow-hidden">
                  <img src={i === 0 ? c.IMAGES.datacenter : c.IMAGES.team} alt={co.name} className="h-full w-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-navy/80 to-transparent" />
                  <div className="absolute bottom-5 start-6 text-white"><p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-300">{co.region}</p><h3 className="mt-1 font-heading text-2xl font-bold">{co.name}</h3></div>
                </div>
                <div className="flex flex-1 flex-col p-8">
                  <p className="flex items-center gap-2 text-sm font-medium text-slate-500"><MapPin className="h-4 w-4 text-brand-teal" />{co.location}</p>
                  <p className="mt-4 flex-1 leading-relaxed text-slate-600">{co.description}</p>
                  <div className="mt-6 flex flex-wrap gap-2">{co.highlights.map((h) => <span key={h} className="rounded-full bg-brand-teal/10 px-3 py-1 text-xs font-semibold text-brand-teal">{h}</span>)}</div>
                  <p className="mt-6 text-sm font-semibold text-brand-orange" dir="ltr">{co.website}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
      <section className="relative overflow-hidden bg-brand-navy py-24 text-white">
        <div className="absolute inset-0 hero-grid opacity-30" />
        <Container className="relative">
          <SectionHeading light eyebrow={g.milestones} title={g.journey} />
          <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {g.timeline.map(([year, text], i) => (
              <Reveal key={year} delay={i * 100} className="rounded-2xl border border-white/10 bg-white/5 p-7 backdrop-blur">
                <p className="flex items-center gap-2 font-heading text-3xl font-bold text-brand-orange"><Calendar className="h-5 w-5" />{year}</p>
                <p className="mt-4 text-sm leading-relaxed text-slate-300">{text}</p>
              </Reveal>
            ))}
          </div>
        </Container>
      </section>
    </>
  );
};
